import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

import { api, formatApiError } from "@/lib/api";
import ReportPageShell from "@/components/reports/ReportPageShell";
import DateRangePicker, { rangeToParams } from "@/components/reports/DateRangePicker";
import ExportDropdown from "@/components/reports/ExportDropdown";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntityCombobox } from "@/components/ui/EntityCombobox";
import { useWithBack } from "@/lib/nav";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatDate } from "@/lib/format";

const CHART_COLOR = "hsl(var(--primary))";

export default function MachinesFrequencyReport() {
  const withBackHref = useWithBack();
  const [range, setRange] = useState({ preset: "ytd", from: null, to: null });
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [customerId, setCustomerId] = useState("all");
  const [siteId, setSiteId] = useState("all");
  const [data, setData] = useState({ top_serviced: [], never_serviced: [] });
  const [loading, setLoading] = useState(true);
  const reqIdRef = useRef(0);

  useEffect(() => {
    api.get("/customers", { params: { active: true, limit: 500 } })
      .then(({ data }) => setCustomers(data.items || []))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  useEffect(() => {
    if (customerId === "all") { setSites([]); setSiteId("all"); return; }
    api.get("/sites", { params: { customer_id: customerId, active: true, limit: 500 } })
      .then(({ data }) => setSites(data.items || []))
      .catch(() => setSites([]));
  }, [customerId]);

  const load = useCallback(async () => {
    const my = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = { ...rangeToParams(range) };
      if (customerId !== "all") params.customer_id = customerId;
      if (siteId !== "all") params.site_id = siteId;
      const { data } = await api.get("/reports/machines-service-frequency", { params });
      if (my !== reqIdRef.current) return;
      setData(data);
    } catch (err) {
      if (my !== reqIdRef.current) return;
      toast.error(formatApiError(err));
    } finally {
      if (my === reqIdRef.current) setLoading(false);
    }
  }, [range, customerId, siteId]);

  useEffect(() => { load(); }, [load]);

  const exportParams = { ...rangeToParams(range) };
  if (customerId !== "all") exportParams.customer_id = customerId;
  if (siteId !== "all") exportParams.site_id = siteId;

  const chart = (
    <div style={{ width: "100%", height: Math.max(240, data.top_serviced.length * 22 + 40) }}>
      <ResponsiveContainer>
        <BarChart data={data.top_serviced} layout="vertical" margin={{ left: 60, right: 20, top: 10, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
          <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
          <YAxis type="category" dataKey="machine_summary" stroke="hsl(var(--muted-foreground))" fontSize={11} width={200} />
          <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
          <Bar dataKey="jobs_count" fill={CHART_COLOR} radius={[0, 3, 3, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const tabs = (
    <Tabs defaultValue="top" className="p-3">
      <TabsList className="rounded-lg bg-muted p-1">
        <TabsTrigger value="top" data-testid="machines-freq-tab-top">Top serviced ({data.top_serviced.length})</TabsTrigger>
        <TabsTrigger value="never" data-testid="machines-freq-tab-never">Never serviced ({data.never_serviced.length})</TabsTrigger>
      </TabsList>
      <TabsContent value="top" className="mt-3">
        <table className="w-full text-sm" data-testid="machines-freq-top-table">
          <thead className="bg-muted/50 border-b border-border">
            <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <th className="text-left py-2 px-3">Machine</th>
              <th className="text-left py-2 px-3">Customer</th>
              <th className="text-left py-2 px-3">Site</th>
              <th className="text-right py-2 px-3">Jobs</th>
              <th className="text-left py-2 px-3">Last serviced</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="py-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
            ) : data.top_serviced.length === 0 ? (
              <tr><td colSpan={5} className="py-8 text-center text-xs text-muted-foreground">No serviced machines in range.</td></tr>
            ) : data.top_serviced.map((m) => (
              <tr key={m.machine_id} data-testid={`machines-freq-top-row-${m.machine_id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                <td className="py-2 px-3"><Link to={withBackHref(`/machines/${m.machine_id}`)} className="hover:text-primary" data-testid={`machines-freq-machine-link-${m.machine_id}`}>{m.machine_summary}</Link></td>
                <td className="py-2 px-3 text-muted-foreground">{m.customer || "—"}</td>
                <td className="py-2 px-3 text-muted-foreground">{m.site || "—"}</td>
                <td className="py-2 px-3 text-right tabular-nums">{m.jobs_count}</td>
                <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{formatDate(m.last_serviced_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TabsContent>
      <TabsContent value="never" className="mt-3">
        <table className="w-full text-sm" data-testid="machines-freq-never-table">
          <thead className="bg-muted/50 border-b border-border">
            <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <th className="text-left py-2 px-3">Machine</th>
              <th className="text-left py-2 px-3">Customer</th>
              <th className="text-left py-2 px-3">Site</th>
              <th className="text-left py-2 px-3">Installed</th>
              <th className="text-right py-2 px-3">Days since install</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="py-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
            ) : data.never_serviced.length === 0 ? (
              <tr><td colSpan={5} className="py-8 text-center text-xs text-muted-foreground">All machines have been serviced.</td></tr>
            ) : data.never_serviced.map((m) => (
              <tr key={m.machine_id} data-testid={`machines-freq-never-row-${m.machine_id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                <td className="py-2 px-3"><Link to={withBackHref(`/machines/${m.machine_id}`)} className="hover:text-primary" data-testid={`machines-freq-machine-link-${m.machine_id}`}>{m.machine_summary}</Link></td>
                <td className="py-2 px-3 text-muted-foreground">{m.customer || "—"}</td>
                <td className="py-2 px-3 text-muted-foreground">{m.site || "—"}</td>
                <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{m.install_date ? formatDate(m.install_date) : "—"}</td>
                <td className="py-2 px-3 text-right tabular-nums">{m.days_since_install ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TabsContent>
    </Tabs>
  );

  return (
    <ReportPageShell
      testId="machines-freq-report"
      title="Machines Service Frequency"
      subtitle="Which machines see the most (and least) service."
      headerActions={
        <ExportDropdown
          basePath="/reports/machines-service-frequency"
          params={exportParams}
          filename="machines-frequency"
          testId="machines-freq-export"
        />
      }
      filters={
        <>
          <DateRangePicker value={range} onChange={setRange} testId="machines-freq-range" />
          <div className="w-56">
            <EntityCombobox
              items={customers}
              value={customerId === "all" ? "" : customerId}
              onChange={(v) => setCustomerId(v || "all")}
              placeholder="All customers"
              emptyMessage="No customers match."
              allowClear
              clearLabel="All customers"
              testid="machines-freq-customer"
              triggerClassName="h-8 text-xs"
            />
          </div>
          {customerId !== "all" && (
            <div className="w-48">
              <EntityCombobox
                items={sites}
                value={siteId === "all" ? "" : siteId}
                onChange={(v) => setSiteId(v || "all")}
                placeholder="All sites"
                emptyMessage="No sites match."
                allowClear
                clearLabel="All sites"
                testid="machines-freq-site"
                triggerClassName="h-8 text-xs"
              />
            </div>
          )}
        </>
      }
      chartTitle="Top serviced (up to 25)"
      chart={chart}
      table={tabs}
    />
  );
}
